import crossFetch from "cross-fetch";
import { Action, MiddlewareAPI } from "redux";
import { extractErrorMessage, parseResponseBody } from "../utils/httpResponse";
import { applyQueryRefreshForPayload } from "./middlewares/licenseInfoRefresh";
import type { ReduxActionMeta } from "./types/ActionMeta";

export const API_CALL_REQUEST = "API_CALL_REQUEST";
export const API_CALL_SUCCESS = "API_CALL_SUCCESS";
export const API_CALL_ERROR = "API_CALL_ERROR";
export const API_CALL_EXCEPTION = "API_CALL_EXCEPTION";
export const API_CALL_TIMING = "API_CALL_TIMING";

export type IApiCallbacks = {
    request?: () => void;
    success?: (response) => void;
    error?: (error: string) => void;
    exception?: (exception: Error) => void;
    finally?: () => void;
};

export type IApiError<T> = {
    error: string;
    response: T;
};

// Serializable representation of a Response object
export type ISerializableResponse = {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    url: string;
    ok: boolean;
};

// Serializable representation of an Error object
export type ISerializableError = {
    name: string;
    message: string;
    stack?: string;
};

export type IApiCallSuccess = Action<string> & {
    url: string;
    description: string;
    payload: ISerializableResponse;
    time: number;
};

export type IApiCallError = Action<string> & {
    url: string;
    httpCode: number;
    description: string;
    payload: IApiError<string>;
    time: number;
};

export type IApiCallException = Action<string> & {
    url: string;
    description: ISerializableError;
    time: number;
};

export type IApiCallTiming = Action & {
    url: string;
    time: number;
};

export type QueryAwareRequestInit = RequestInit & {
    queryRefreshMeta?: ReduxActionMeta;
};

// Helper function to create serializable response object
const createSerializableResponse = (
    response: Response
): ISerializableResponse => {
    const headers: Record<string, string> = {};
    response.headers?.forEach((value, key) => {
        headers[key] = value;
    });

    return {
        status: response.status,
        statusText: response.statusText,
        headers,
        url: response.url,
        ok: response.ok,
    };
};

// Helper function to create serializable error object
const createSerializableError = (error: unknown): ISerializableError => {
    if (error instanceof Error) {
        return {
            name: error.name,
            message: error.message,
            stack: error.stack,
        };
    }

    // Handle case where error is not an Error object
    return {
        name: "UnknownError",
        message: String(error),
    };
};

export const createFetchInstance = (store: MiddlewareAPI) => {
    const parseJsonResponseClone = async (
        response: Response
    ): Promise<unknown> => {
        try {
            return await response.clone().json();
        } catch {
            return undefined;
        }
    };

    const applyQueryRefresh = async (
        response: Response,
        meta?: ReduxActionMeta
    ): Promise<void> => {
        if (!meta) {
            return;
        }
        const payload = await parseJsonResponseClone(response);
        applyQueryRefreshForPayload(store, payload, meta);
    };

    const handleSuccess = (json: unknown, callbacks?: IApiCallbacks) => {
        if (callbacks) {
            if (callbacks.success) {
                callbacks.success(json);
            }
            if (callbacks.finally) {
                callbacks.finally();
            }
        }
    };

    const handleError = (
        error: IApiError<string> | string,
        callbacks?: IApiCallbacks
    ) => {
        if (callbacks) {
            if (callbacks.error) {
                callbacks.error(
                    typeof error === "string" ? error : error.response
                );
            }
            if (callbacks.finally) {
                callbacks.finally();
            }
        }
    };

    const handleException = (exception: Error, callbacks?: IApiCallbacks) => {
        if (callbacks) {
            if (callbacks.exception) {
                callbacks.exception(exception);
            }
            if (callbacks.finally) {
                callbacks.finally();
            }
        }
    };

    const customFetch = (
        url: string,
        init?: QueryAwareRequestInit
    ): Promise<Response> => {
        const timestamp: number = performance.now();
        const { queryRefreshMeta, ...requestInit } = init ?? {};

        const state = store.getState();
        if (state && state.user) {
            requestInit.headers = {
                ...requestInit.headers,
                // ingress-nginx doesn't like underscores in headers
                "csrf-token": state.user.csrf_token,
            };
        }

        store.dispatch({
            type: API_CALL_REQUEST,
            url,
            payload: requestInit,
        });

        return crossFetch(url, requestInit)
            .then((res) => {
                const error = !res.ok;
                const responseTime = performance.now() - timestamp;

                store.dispatch({
                    type: API_CALL_TIMING,
                    url,
                    time: responseTime,
                });

                if (error) {
                    store.dispatch({
                        type: API_CALL_ERROR,
                        url,
                        httpCode: res.status,
                        description: res.statusText,
                        payload: {
                            error: res.statusText,
                            response: res.status.toString(),
                        },
                        time: responseTime,
                    });
                } else {
                    store.dispatch({
                        type: API_CALL_SUCCESS,
                        url,
                        description: res.statusText,
                        payload: createSerializableResponse(res),
                        time: responseTime,
                    });
                }

                return applyQueryRefresh(res, queryRefreshMeta).then(() => res);
            })
            .catch((error: unknown) => {
                const responseTime = performance.now() - timestamp;

                store.dispatch({
                    type: API_CALL_EXCEPTION,
                    url,
                    description: createSerializableError(error),
                    time: responseTime,
                });

                throw error;
            });
    };

    return {
        fetch: customFetch,
        apiCall: (
            url: string,
            data?: unknown,
            callbacks?: IApiCallbacks,
            options: QueryAwareRequestInit = { method: "POST" }
        ) => {
            Object.assign(options, { credentials: "same-origin" });

            if (!options.headers) {
                options.headers = {};
            }

            if (data) {
                if (data instanceof Object) {
                    data = JSON.stringify(data);
                }
                options.body = String(data);
            }

            const promise = customFetch(url, options)
                .then(async (res) => {
                    const payload = await parseResponseBody(res);
                    if (!res.ok) {
                        return handleError(
                            extractErrorMessage(payload) ||
                                res.statusText ||
                                `Request failed (${res.status})`,
                            callbacks
                        );
                    }
                    return handleSuccess(payload, callbacks);
                })
                .catch((err) => handleException(err, callbacks));

            if (callbacks && callbacks.request) {
                callbacks.request();
            }

            return promise;
        },
    };
};
